'use client'

import { Listbox, Popover } from '@headlessui/react'
import { ChevronUpIcon, MenuIcon, TranslateIcon } from '@heroicons/react/outline'
import { ChevronDownIcon } from '@heroicons/react/solid'
import Button from 'components/base/Button'
import Link from 'components/base/Link'
import Tooltip from 'components/base/Tooltip'
import { links } from 'configs/links'
import { AnimatePresence, motion } from 'motion/react'
import { c, FC } from 'lib/component-utils'
import { useUserContext } from 'lib/context'
import { useIntl } from 'lib/intl-utils'
import { useEffect, useRef, useState } from 'react'
import { usePromoCodeContext } from './lib/promoCodeContext'
import { useTrackGoal } from 'lib/metrics'
import PromoCode from 'components/landing/PromoCode'
import { useRouter } from 'next/router'
import { setCookie } from 'nookies'
import Select from 'components/base/Select'
import { match } from 'ts-pattern'
import { useTheme } from 'lib/colors'

const MobileNavLink: FC<{ href: string; name: string }, typeof Link> = ({
  children,
  name,
  href,
  ...props
}) => {
  const trackGoal = useTrackGoal()
  const onClick = name === 'telegram_bot' ? () => trackGoal('moved-to-telegram-bot') : () => {}
  const isInternal = href.startsWith('/') && !href.startsWith('/help')
  const LinkComponent = isInternal ? Link : 'a'

  const linkProps = isInternal ? {} : { target: '_blank', rel: 'noreferrer' }

  return (
    // @ts-ignore
    <Popover.Button
      as={LinkComponent as any}
      {...linkProps}
      href={href}
      onClick={onClick}
      className="block text-base leading-7 tracking-tight text-gray-700"
      {...props}
    >
      {children}
    </Popover.Button>
  )
}

const blurLayers = [
  {
    blur: 0.5,
    mask: 'linear-gradient(to top, transparent 0%, black 12.5%, black 25%, transparent 37.5%)',
  },
  {
    blur: 1,
    mask: 'linear-gradient(to top, transparent 12.5%, black 25%, black 37.5%, transparent 50%)',
  },
  {
    blur: 2,
    mask: 'linear-gradient(to top, transparent 25%, black 37.5%, black 50%, transparent 62.5%)',
  },
  {
    blur: 3,
    mask: 'linear-gradient(to top, transparent 37.5%, black 50%, black 62.5%, transparent 75%)',
  },
  {
    blur: 4,
    mask: 'linear-gradient(to top, transparent 50%, black 62.5%, black 75%, transparent 87.5%)',
  },
  { blur: 6, mask: 'linear-gradient(to top, transparent 62.5%, black 75%)' },
]

const tintMask = [
  'linear-gradient(to bottom',
  'black 0%',
  'black 40%',
  'rgba(0,0,0,0.94) 47.5%',
  'rgba(0,0,0,0.79) 55%',
  'rgba(0,0,0,0.57) 62.5%',
  'rgba(0,0,0,0.35) 70%',
  'rgba(0,0,0,0.17) 77.5%',
  'rgba(0,0,0,0.06) 85%',
  'rgba(0,0,0,0.01) 92.5%',
  'transparent 100%)',
].join(', ')

export const ProgressiveBlur: FC<{ onDark: boolean }> = ({ onDark }) => (
  <div aria-hidden="true" className="absolute inset-0 -z-10 pointer-events-none">
    <div className="absolute inset-x-0 top-0 -bottom-8">
      {blurLayers.map(({ blur, mask }) => (
        <div
          key={blur}
          className="absolute inset-0"
          style={{
            backdropFilter: `blur(${blur}px)`,
            WebkitBackdropFilter: `blur(${blur}px)`,
            maskImage: mask,
            WebkitMaskImage: mask,
          }}
        />
      ))}
    </div>
    <div
      className={c`absolute inset-x-0 top-0 -bottom-6 bg-white/80 transition-opacity duration-300 ${onDark} opacity-0`}
      style={{ maskImage: tintMask, WebkitMaskImage: tintMask }}
    />
    <div
      className={c`absolute inset-x-0 top-0 -bottom-6 bg-gray-900/80 transition-opacity duration-300 ${onDark} | opacity-0`}
      style={{ maskImage: tintMask, WebkitMaskImage: tintMask }}
    />
  </div>
)

type Props = {
  showSignupButton?: boolean
}

const Header: FC<Props> = ({ showSignupButton = false }) => {
  const ctx = useUserContext()
  const promoCode = usePromoCodeContext()
  const { t, lang } = useIntl()
  const trackGoal = useTrackGoal()
  const router = useRouter()
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [isPinned, setIsPinned] = useState(false)
  const [headerHeight, setHeaderHeight] = useState(0)
  const [isOverDark, setIsOverDark] = useState(false)
  const stickyMarkerRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  const isSticky = ctx.theme.hostType === 'sellmonitor' && !ctx.isAuthorized
  const { isDark } = useTheme()
  const onDark = isPinned && isOverDark

  useEffect(() => {
    if (!isSticky || !stickyMarkerRef.current) return

    const observer = new IntersectionObserver(([entry]) => {
      const shouldPin = !entry.isIntersecting && entry.boundingClientRect.top < 0
      if (
        shouldPin &&
        headerRef.current &&
        getComputedStyle(headerRef.current).position !== 'fixed'
      ) {
        setHeaderHeight(headerRef.current.offsetHeight)
      }
      setIsPinned(shouldPin)
    })
    observer.observe(stickyMarkerRef.current)

    const updateSpacerOnResize = () => {
      if (headerRef.current && getComputedStyle(headerRef.current).position === 'fixed') {
        setHeaderHeight(headerRef.current.offsetHeight)
      }
    }
    window.addEventListener('resize', updateSpacerOnResize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', updateSpacerOnResize)
    }
  }, [isSticky])

  useEffect(() => {
    if (!isPinned) return

    let frame = 0
    const update = () => {
      frame = 0
      const y = (headerRef.current?.offsetHeight ?? 0) / 2
      const sections = document.querySelectorAll('[data-header-theme="dark"]')
      setIsOverDark(
        Array.from(sections).some((section) => {
          const { top, bottom } = section.getBoundingClientRect()
          return top <= y && bottom >= y
        })
      )
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)

    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [isPinned])

  const setLanguage = (code: string) => {
    setCookie(null, 'lang', code, {
      maxAge: 365 * 24 * 60 * 60,
      path: '/',
    })
    router.reload()
  }

  return (
    <>
      {isSticky && <div ref={stickyMarkerRef} aria-hidden="true" className="h-5" />}
      {isSticky && isPinned && <div aria-hidden="true" style={{ height: headerHeight }} />}
      <header
        ref={headerRef}
        className={c`${isSticky && !isPinned} sticky top-0 z-[70] bg-white ${isSticky && isPinned} fixed inset-x-0 top-0 z-[70]`}
      >
        {isSticky && isPinned && <ProgressiveBlur onDark={onDark} />}
        <nav>
          <div
            className={c`relative z-50 flex justify-between px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 ${isSticky} py-3 | py-8`}
          >
            <div
              className={c`relative z-10 flex items-center gap-8 sm:gap-16 ${
                promoCode !== null
              } lg:gap-8 | lg:gap-16`}
            >
              <Link href="/" aria-label="Home" className="shrink-0">
                <span className="relative block">
                  <img
                    src={isDark ? ctx.theme.logoWhite || ctx.theme.logoDark : ctx.theme.logoDark}
                    alt={ctx.theme.title}
                    className={c`w-auto h-6 transition-opacity duration-300 ${onDark} opacity-0`}
                  />
                  {ctx.theme.logoWhite && (
                    <img
                      src={ctx.theme.logoWhite}
                      alt=""
                      aria-hidden="true"
                      className={c`absolute inset-0 w-auto h-6 transition-opacity duration-300 ${onDark} | opacity-0`}
                    />
                  )}
                </span>
                {ctx.theme.hostType === 'sellscreen' && lang === 'zh' && (
                  <span className="block mt-1 -mb-4 text-lg font-medium">赛讴选品</span>
                )}
              </Link>
              <div
                className={c`hidden lg:gap-8 [@media(min-width:1150px)]:gap-10 ${
                  promoCode !== null
                } xl:flex | [@media(min-width:1150px)]:flex`}
              >
                {links.map(({ name, href, hosts, links }, index) => {
                  if (!hosts.includes(ctx.theme.hostType)) return null
                  if (name === 'blog' && lang === 'en') return null

                  if (links) {
                    return (
                      <Tooltip
                        key={name}
                        sizing="custom"
                        className="overflow-hidden rounded-lg"
                        side="bottom"
                        align="start"
                        content={
                          <div>
                            {links.map(({ name, href }) => {
                              const onClick =
                                name === 'telegram_bot'
                                  ? () => trackGoal('moved-to-telegram-bot')
                                  : () => {}
                              const isInternal = href.startsWith('/') && !href.startsWith('/help')
                              const LinkComponent = isInternal ? Link : 'a'

                              const linkProps = isInternal
                                ? {}
                                : { target: '_blank', rel: 'noreferrer' }

                              return (
                                <LinkComponent
                                  {...linkProps}
                                  key={name}
                                  href={href ?? '#'}
                                  onClick={onClick}
                                  className="block w-full hover:bg-gray-100 py-2.5 px-4"
                                >
                                  <span className="relative z-10 whitespace-nowrap">
                                    {t(`links.${name}`)}
                                  </span>
                                </LinkComponent>
                              )
                            })}
                          </div>
                        }
                      >
                        <div
                          key={name}
                          className={c`cursor-pointer relative -my-2 -mx-3 rounded-lg px-3 py-2 text-sm transition-colors delay-150 hover:delay-[0ms] ${onDark} text-gray-300 hover:text-white | text-gray-700 hover:text-gray-900`}
                          onMouseEnter={() => setHoveredIndex(index)}
                          onMouseLeave={() => setHoveredIndex(null)}
                        >
                          <AnimatePresence>
                            {hoveredIndex === index && (
                              <motion.span
                                className={c`absolute inset-0 rounded-lg ${onDark} bg-white/10 | bg-gray-100`}
                                layoutId="hoverBackground"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1, transition: { duration: 0.15 } }}
                                exit={{
                                  opacity: 0,
                                  transition: { duration: 0.15, delay: 0.2 },
                                }}
                              />
                            )}
                          </AnimatePresence>
                          <span className="relative z-10 inline-flex items-center space-x-1 whitespace-nowrap">
                            <span>{t(`links.${name}`)}</span>
                            <ChevronDownIcon className="w-4 h-4" />
                          </span>
                        </div>
                      </Tooltip>
                    )
                  }

                  const onClick =
                    name === 'telegram_bot' ? () => trackGoal('moved-to-telegram-bot') : () => {}
                  const isInternal = href.startsWith('/') && !href.startsWith('/help')
                  const LinkComponent = isInternal ? Link : 'a'

                  const linkProps = isInternal ? {} : { target: '_blank', rel: 'noreferrer' }

                  return (
                    <LinkComponent
                      {...linkProps}
                      key={name}
                      href={href ?? '#'}
                      onClick={onClick}
                      className={c`relative -my-2 -mx-3 rounded-lg px-3 py-2 text-sm transition-colors delay-150 hover:delay-[0ms] ${onDark} text-gray-300 hover:text-white | text-gray-700 hover:text-gray-900`}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <AnimatePresence>
                        {hoveredIndex === index && (
                          <motion.span
                            className={c`absolute inset-0 rounded-lg ${onDark} bg-white/10 | bg-gray-100`}
                            layoutId="hoverBackground"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { duration: 0.15 } }}
                            exit={{
                              opacity: 0,
                              transition: { duration: 0.15, delay: 0.2 },
                            }}
                          />
                        )}
                      </AnimatePresence>
                      <span className="relative z-10 whitespace-nowrap">{t(`links.${name}`)}</span>
                    </LinkComponent>
                  )
                })}
              </div>
              {promoCode && (
                <div className="[@media(max-width:400px)]:hidden">
                  <PromoCode promoCode={promoCode} compact />
                </div>
              )}
            </div>
            <div className="flex items-center gap-6">
              {ctx.theme.hostType === 'sellematics' && (
                <Select<'en' | 'ru'>
                  selected={lang as 'en' | 'ru'}
                  setSelected={setLanguage}
                  dropdownClassName="right-0"
                  options={[lang as 'en' | 'ru', lang === 'en' ? 'ru' : 'en']}
                  display={(value: 'en' | 'ru') =>
                    match(value)
                      .with('en', () => 'English')
                      .with('ru', () => 'Русский')
                      .exhaustive()
                  }
                  button={() => (
                    <Listbox.Button className="flex items-center px-3 py-2 space-x-2 rounded-md hover:bg-gray-100">
                      <TranslateIcon className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">
                        {match(lang as 'en' | 'ru')
                          .with('en', () => 'English')
                          .with('ru', () => 'Русский')
                          .exhaustive()}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </Listbox.Button>
                  )}
                />
              )}
              {ctx.theme.hostType === 'sellscreen' && (
                <Select<'en' | 'zh'>
                  selected={lang as 'en' | 'zh'}
                  setSelected={setLanguage}
                  dropdownClassName="right-0"
                  options={[lang as 'en' | 'zh', lang === 'en' ? 'zh' : 'en']}
                  display={(value: 'en' | 'zh') =>
                    match(value)
                      .with('en', () => 'English')
                      .with('zh', () => '中文')
                      .exhaustive()
                  }
                  button={() => (
                    <Listbox.Button className="flex items-center px-3 py-2 space-x-2 rounded-md hover:bg-gray-100">
                      <TranslateIcon className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">
                        {match(lang as 'en' | 'zh')
                          .with('en', () => 'English')
                          .with('zh', () => '中文')
                          .exhaustive()}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </Listbox.Button>
                  )}
                />
              )}
              {ctx.theme.hostType === 'uzum' && (
                <Select<'ru' | 'uz'>
                  selected={lang as 'ru' | 'uz'}
                  setSelected={setLanguage}
                  dropdownClassName="right-0"
                  options={[lang as 'ru' | 'uz', lang === 'ru' ? 'uz' : 'ru']}
                  display={(value: 'ru' | 'uz') =>
                    match(value)
                      .with('uz', () => "O'zbek")
                      .with('ru', () => 'Русский')
                      .exhaustive()
                  }
                  button={() => (
                    <Listbox.Button className="flex items-center px-3 py-2 space-x-2 rounded-md hover:bg-gray-100">
                      <TranslateIcon className="w-4 h-4 text-gray-500" />
                      <span className="font-medium text-gray-700">
                        {match(lang as 'ru' | 'uz')
                          .with('uz', () => "O'zbek")
                          .with('ru', () => 'Русский')
                          .exhaustive()}
                      </span>
                      <ChevronDownIcon className="w-4 h-4 text-gray-500" />
                    </Listbox.Button>
                  )}
                />
              )}
              <Popover
                className={c`${promoCode !== null} xl:hidden | [@media(min-width:1150px)]:hidden`}
              >
                {({ open }) => (
                  <>
                    <Popover.Button
                      className={c`relative z-10 translate-y-[3px] -m-2 inline-flex items-center rounded-lg stroke-gray-900 p-2 transition-colors duration-300 hover:stroke-gray-600 active:stroke-gray-900 [&:not(:focus-visible)]:focus:outline-none ${onDark} text-white hover:bg-white/10 | hover:bg-gray-200/50`}
                      aria-label="Toggle site navigation"
                    >
                      {({ open }) =>
                        open ? (
                          <ChevronUpIcon className="w-6 h-6" />
                        ) : (
                          <MenuIcon className="w-6 h-6" />
                        )
                      }
                    </Popover.Button>
                    <AnimatePresence initial={false}>
                      {open && (
                        <>
                          <Popover.Overlay
                            static
                            as={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-0 backdrop-blur bg-gray-300/60"
                          />
                          <Popover.Panel
                            static
                            as={motion.div}
                            initial={{ opacity: 0, y: -32 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{
                              opacity: 0,
                              y: -32,
                              transition: { duration: 0.2 },
                            }}
                            className={c`inset-x-0 top-0 px-6 origin-top shadow-2xl bg-gray-50 ${isSticky} fixed bottom-0 z-20 overflow-y-auto overscroll-contain pb-[max(1.5rem,env(safe-area-inset-bottom))] | absolute z-0 pt-32 pb-6 rounded-b-2xl shadow-gray-900/20`}
                          >
                            {isSticky && (
                              <div className="sticky top-0 z-10 flex items-center justify-between -mx-6 px-6 py-3 bg-gray-50">
                                <Link href="/" aria-label="Home">
                                  <img
                                    src={ctx.theme.logoDark}
                                    alt={ctx.theme.title}
                                    className="w-auto h-6"
                                  />
                                </Link>
                                <Popover.Button
                                  className="-m-2 rounded-lg p-2 stroke-gray-900 hover:bg-gray-200/50"
                                  aria-label="Закрыть меню"
                                >
                                  <ChevronUpIcon className="w-6 h-6" />
                                </Popover.Button>
                              </div>
                            )}
                            <div className={c`space-y-4 ${isSticky} mt-6`}>
                              {links
                                .flatMap((link) => {
                                  if (!link.hosts.includes(ctx.theme.hostType)) return []
                                  if (link.name === 'blog' && lang === 'en') return []
                                  return link.href ? [link] : link.links!
                                })
                                .map(({ name, href }) => {
                                  return (
                                    <MobileNavLink key={name} name={name} href={href ?? '#'}>
                                      {t(`links.${name}`)}
                                    </MobileNavLink>
                                  )
                                })}
                            </div>
                            {promoCode && (
                              <div className="hidden mt-6 [@media(max-width:400px)]:block">
                                <PromoCode promoCode={promoCode} compact />
                              </div>
                            )}
                            <div className="flex flex-col gap-4 mt-8">
                              <Button as={Link} theme="white" href="/login/">
                                {t`landing.log_in`}
                              </Button>
                              {!['sellematics', 'uzum'].includes(ctx.theme.hostType) && (
                                <Button
                                  as={Link}
                                  theme="accent"
                                  href="/signup/"
                                  onClick={() => trackGoal('landing-clicked-register')}
                                >
                                  {t`landing.try_for_free`}
                                </Button>
                              )}
                            </div>
                          </Popover.Panel>
                        </>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </Popover>
              <Button as={Link} theme="white" href="/login" className="hidden lg:block">
                {t`landing.log_in`}
              </Button>
              {!showSignupButton && !['sellematics'].includes(ctx.theme.hostType) && (
                <Button
                  as={Link}
                  theme="primary"
                  href="/signup/"
                  className="hidden lg:block"
                  onClick={() => trackGoal('landing-clicked-register-header')}
                >
                  {t`sign_up`}
                </Button>
              )}
              {showSignupButton && (
                <Button as={Link} theme="accent" href="/signup/" className="hidden lg:block">
                  {t`landing.try_for_free`}
                </Button>
              )}
            </div>
          </div>
        </nav>
      </header>
      {isSticky && <div aria-hidden="true" className="h-5" />}
    </>
  )
}

export default Header
